"use client";

import { useCallback, useEffect, useId, type RefObject } from "react";

/**
 * Which open surface answers Escape: one registry, not six listeners racing.
 *
 * Six things own a document-level Escape listener: `ui/Dialog`,
 * `ImageLightbox`, `ChatDock`, `ProfileHoverCard`, `ImageCropper`, and the
 * `useDismissable` popovers. When more than one is open exactly one should
 * answer — the one the reader is looking at. Three of the six are in here; the
 * other three are named at the bottom, with the reason each stays out.
 *
 * What does not work, and was tried first (OSO-434): having each listener bail
 * on `e.defaultPrevented` and mark the key handled when it acts. That expresses
 * "innermost wins" only where the surfaces are nested and the inner one
 * registers a capture-phase listener, which is true of `useDismissable` and
 * nothing else. For peers it degrades to document registration order, which is
 * mount order, which is not a fact about what is in front:
 *
 *   - the dock registers when its first window opens and stays registered
 *     while minimized, so it would consume the Escape meant for a composer
 *     opened afterwards;
 *   - the lightbox opened from a message INSIDE a docked conversation
 *     registers after the dock, so the dock would answer for the fullscreen
 *     thing in front of it;
 *   - and `[docks.length]` re-registers the dock whenever a second window
 *     opens or closes, so which surface answers is not even stable within one
 *     session.
 *
 * What decides instead, in this order:
 *
 *   1. Containment. A layer whose panel contains another open layer's panel is
 *      underneath it, whatever their ranks say. This is the rule `ui/Dialog`
 *      already had for stacked dialogs — a confirmation rendered as a child of
 *      the composer registers first, because React flushes passive effects
 *      child-first — generalised so a `Dialog` opened inside the lightbox's
 *      sidebar also wins over the lightbox around it.
 *   2. Rank. `ESCAPE_LAYER` below, and it is not an opinion: it is the z-index
 *      these surfaces are already painted at. The lightbox is `z-[100]`, a
 *      dialog `z-50`, the dock `z-40`. What is drawn on top answers.
 *   3. Registration order, last first, between layers of equal rank that do not
 *      contain one another. Two sibling dialogs: the one opened later. Two
 *      lightboxes, the inner opened from a comment in the outer one's sidebar:
 *      the inner. Neither contains the other — both portal to `document.body` —
 *      so this is the rule that separates them.
 *
 * Three of the six are deliberately not in here. Two for the same reason: their
 * listeners are capture-phase, so they already answer before every registered
 * one below, and `preventDefault()` is how they say so.
 *
 *   - `useDismissable` — the dropdowns and menus. Joining would buy nothing
 *     and cost the one thing that makes them correct.
 *   - `ImageCropper` (`ImageCropper.tsx:70-78`) — Escape while cropping
 *     cancels the crop unconditionally and is spent doing it, which is what
 *     stops it reaching the `Dialog` the overlay is a DOM CHILD of at its
 *     `EventComposer` call site. It reads neither `defaultPrevented` nor this
 *     registry, and does not need to: phase, not rank, is what puts it first.
 *
 * The third is the hover card, out on purpose at a known cost.
 * `ProfileHoverCard` is `z-[200]`, above every layer here, so by the reasoning
 * above it belongs in the registry — and for one round of OSO-434 it was, along
 * with the second copy of the same panel that `PostHtml` portals for an
 * `@mention`. What made that not worth its price is that the card opens on
 * `focus` as well as on hover: registering every open card puts a layer at the
 * top rank on every author name a keyboard user tabs past, unasked, and each
 * attempt to register only the cards the reader actually asked for turned up
 * another way for that flag to be wrong (a click on the card's own "Send
 * besked" focuses it from inside; the mention card's coords go stale). Review
 * round after review round found another, while the arbitration above held;
 * when round six found two more, it came out. What stands now is what stood
 * before OSO-434: the wrapper closes on Escape without claiming the key, and
 * the `@mention` card does not answer Escape at all.
 *
 *   The cost, knowingly accepted: one Escape dismisses the card and whatever is
 *   under it. `ProfileHoverCard.tsx` closes on Escape without consulting this
 *   registry or `e.defaultPrevented`, so both halves of the arbitration pass it
 *   by, and the double-dismiss runs in both directions:
 *
 *     - The card cannot claim the key from a layer. Hover a name in a
 *       lightbox's comment sidebar: both listeners are bubble-phase on
 *       `document` and the lightbox registered first (it is the ancestor
 *       surface, open before the card), so `preventDefault()` from the card
 *       could not reach it even if it called one — it only reaches listeners
 *       registered later. Card and lightbox close together.
 *     - And a layer cannot claim the key from the card, which is not in the
 *       registry to be outranked and does not read the flag. Hover a name,
 *       click "Send besked": the chat window docks under a card still sitting
 *       beneath the pointer, and one Escape closes both — `ChatDock` is topmost
 *       among registered layers and sees `defaultPrevented === false`. Same
 *       shape for an Escape that cancels a reply: `RichTextInput` marks the key
 *       spent, every registered layer stands down, and the card closes anyway.
 *
 *   A decision, not an oversight — the price of keeping a focus-opened card out
 *   of the registry, and what stood before OSO-434. OSO-458 tracks the card's
 *   keyboard behaviour separately, against the behaviour standing here.
 *
 * The three are named because a file positioning itself as the arbiter has to
 * say what it does not arbitrate. The registered layers still read
 * `e.defaultPrevented`, which is how a dropdown drawn over any of them keeps
 * the Escape that closed it.
 *
 * Claiming the key is not a substitute for joining: a `preventDefault()` from a
 * bubble-phase listener only reaches surfaces that registered later, and the
 * layer a surface is drawn inside registered earlier by construction. Anything
 * that can be on top of a layer belongs in here unless, like the three above,
 * there is a stated reason it is not.
 *
 * And this decides Escape, not modality. "Topmost" here means topmost among all
 * surfaces, the right question for a key that dismisses one thing and the wrong
 * one for `ui/Dialog`'s focus trap, which must keep holding Tab while a
 * non-modal thing is drawn over it. That is what the `rank` scope on the
 * predicate below is for; `ImageLightbox` asks it about its arrow keys for the
 * same reason.
 */

/**
 * The stacking order, mirroring the z-index each surface is painted at. Bigger
 * is nearer the reader.
 */
export const ESCAPE_LAYER = {
  /** `ChatDock` — `z-40`. */
  dock: 10,
  /** `ui/Dialog` — `z-50`. */
  dialog: 20,
  /** `ImageLightbox` — `z-[100]`, the highest registered surface. */
  lightbox: 30,
} as const;

interface Layer {
  id: string;
  rank: number;
  /** The element that IS this layer, where it has one. Drives containment. */
  panel: RefObject<HTMLElement | null> | undefined;
}

const layers: Layer[] = [];

/**
 * `onlyRank` narrows the field to one layer kind and answers within it —
 * containment included, so two nested dialogs still order correctly. It is how
 * a question about MY OWN KIND ("is another dialog over this one, so should its
 * focus trap stand down?" / "is another lightbox over this one, so are the
 * arrow keys its?") stays separate from the question about Escape ("is
 * anything at all over this one?").
 */
function topmostEscapeLayerId(onlyRank?: number): string | undefined {
  const field =
    onlyRank === undefined ? layers : layers.filter((l) => l.rank === onlyRank);
  let best: Layer | undefined;
  for (const candidate of field) {
    const el = candidate.panel?.current ?? null;
    const holdsAnother =
      el !== null &&
      field.some(
        (other) =>
          other !== candidate &&
          other.panel?.current != null &&
          el.contains(other.panel.current),
      );
    if (holdsAnother) continue;
    // `>=` so the LAST registered wins a tie, which is what "opened later"
    // means for two surfaces neither of which contains the other.
    if (!best || candidate.rank >= best.rank) best = candidate;
  }
  return best?.id;
}

/**
 * Is another registered layer drawn inside `el`? Containment on its own, for
 * the keys that are not Escape.
 *
 * `ImageLightbox` must not gate its arrows on the unscoped
 * `topmostEscapeLayerId` — anything at all drawn over it would kill image
 * navigation, and most of what can be wants nothing to do with those keys — but
 * "nothing at all" was too far the other way: a `Dialog` opened from its sidebar
 * autofocuses a `<button>`, neither editable nor `defaultPrevented`, so an arrow
 * pressed at a confirmation changed the image behind it.
 *
 * This asks the narrower question those arrows have about lower-ranked surfaces
 * — "is a dialog of mine open on me" — off the same registry rather than a
 * second copy of the rule. A peer lightbox is a different question containment
 * cannot answer, since both portal to `document.body`; that one is
 * `isTopmost("rank")`.
 */
export function hasLayerInside(el: HTMLElement | null): boolean {
  if (!el) return false;
  return layers.some(
    (l) =>
      l.panel?.current != null &&
      l.panel.current !== el &&
      el.contains(l.panel.current),
  );
}

/**
 * How the predicate is being asked the question. `"all"` — the default, and
 * what an Escape handler wants — is "nothing at all is drawn over this".
 * `"rank"` is "nothing of MY OWN KIND is drawn over this", which is what a
 * focus trap wants — a `Dialog` stands its trap down for the confirmation
 * stacked on it, and not for a non-modal surface that traps nothing — and what
 * `ImageLightbox`'s arrow keys want, which is the same shape: only another
 * lightbox takes them.
 */
export type EscapeScope = "all" | "rank";

/**
 * Register this surface while `open`, and get back the predicate its keyboard
 * handler should gate on.
 *
 * `panel` is optional: pass it when the surface has one element that everything
 * inside it lives in, so a layer nested in it can out-rank it. A surface with
 * no panel is never demoted by containment and relies on rank alone, which is
 * enough where containment cannot arise: the dock's windows are several
 * elements, but everything that opens inside one out-ranks it.
 */
export function useEscapeLayer(
  open: boolean,
  rank: number,
  panel?: RefObject<HTMLElement | null>,
): (scope?: EscapeScope) => boolean {
  const id = useId();

  useEffect(() => {
    if (!open) return;
    const entry: Layer = { id, rank, panel };
    layers.push(entry);
    return () => {
      const at = layers.indexOf(entry);
      if (at !== -1) layers.splice(at, 1);
    };
  }, [open, id, rank, panel]);

  return useCallback(
    (scope: EscapeScope = "all") =>
      topmostEscapeLayerId(scope === "rank" ? rank : undefined) === id,
    [id, rank],
  );
}
